/**
 * Authenticated fetch wrapper with automatic token refresh.
 *
 * Drop-in replacement for raw fetch() that:
 * 1. Attaches the access_token from localStorage
 * 2. On 401, attempts a single token refresh via /api/v1/auth/refresh
 * 3. Retries the original request with the new token
 * 4. If refresh fails, clears storage and redirects to /login
 * 5. Queues concurrent requests during refresh (same pattern as api.js interceptor)
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

let isRefreshing = false;
let refreshPromise = null;

function getToken() {
  return localStorage.getItem('access_token');
}

function getRefreshToken() {
  return localStorage.getItem('refresh_token');
}

function clearAuthAndRedirect() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('remember_me');
  window.location.href = '/login';
}

async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthAndRedirect();
    throw new Error('No refresh token');
  }

  const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!res.ok) {
    clearAuthAndRedirect();
    throw new Error('Refresh failed');
  }

  const data = await res.json();
  localStorage.setItem('access_token', data.access_token);
  if (data.refresh_token) {
    localStorage.setItem('refresh_token', data.refresh_token);
  }
  if (data.user) {
    localStorage.setItem('user', JSON.stringify(data.user));
  }
  return data.access_token;
}

/**
 * Perform a single, serialized token refresh.
 * All concurrent callers share the same promise.
 */
function refreshOnce() {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = doRefresh().finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Authenticated fetch — use this instead of raw fetch() everywhere.
 *
 * Signature is identical to window.fetch():
 *   fetchWithAuth(url, init?)
 *
 * It injects Authorization header and handles 401 transparently.
 */
export async function fetchWithAuth(url, init = {}) {
  // Attach current token
  const headers = { ...(init.headers || {}) };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, { ...init, headers });

  // If not 401, return as-is
  if (res.status !== 401) {
    return res;
  }

  // 401 — attempt refresh and retry once
  try {
    const newToken = await refreshOnce();
    headers['Authorization'] = `Bearer ${newToken}`;
    return fetch(url, { ...init, headers });
  } catch {
    // refresh failed — clearAuthAndRedirect already called inside doRefresh
    return res; // return original 401 as fallback
  }
}

export default fetchWithAuth;
